import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, onSnapshot } from "firebase/firestore";
import { database } from "../../database/firebase";
import "./leaderboard.css";
import LeaderboardConfetti from "../../components/LeaderboardConfetti";
import icon1 from "../../img/icon1.png";
import icon2 from "../../img/icon2.png";
import icon3 from "../../img/icon3.png";
import icon4 from "../../img/icon4.png";
import icon5 from "../../img/icon5.png";
import icon6 from "../../img/icon6.png";
import icon7 from "../../img/icon7.png";
import icon8 from "../../img/icon8.png";
import MonkeyBackdrop from "../../components/MonkeyBackdrop";
import correct from "../../img/correct.png";
import trophyImage from "../../img/troffy.png";

const LOBBY_COLLECTION = "lobby";
const LOBBY_USERDATA = "userdata";

function readTruthLobby() {
  try {
    const raw = sessionStorage.getItem("truthLobby");
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (!o || typeof o !== "object") return null;
    const sessionId = String(o.sessionId ?? "").trim();
    const lobbyId = String(o.lobbyId ?? "").trim();
    if (!sessionId || !lobbyId) return null;
    return o;
  } catch {
    return null;
  }
}

function displayNameForUser(userId, data) {
  const n = String(data?.name ?? "").trim();
  if (n) return n;
  const id = String(userId ?? "").trim();
  return id ? id.slice(0, 10) : "Player";
}

/**
 * End-of-session scores from Firestore `truthGameScore` on lobby userdata.
 * Route: `/truth-leaderboard` (redirected when `gameFinished` is set on the room).
 */
function Leaderboard({updateHeader}) {
     useEffect(() => {
          updateHeader({
            backButtonRedirect: "/game",
            // ...(companyLogo ? { imageUrl: companyLogo } : {}),
          });
        }, []);
  const navigate = useNavigate();
  const [truthLobby] = useState(() => readTruthLobby());
  const [userMap, setUserMap] = useState(() => new Map());
  const [err, setErr] = useState("");
  const [showConfetti, setShowConfetti] = useState(false);

  // Trigger confetti when component mounts
  useEffect(() => {
    setShowConfetti(true);
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  const sessionId = String(truthLobby?.sessionId ?? "").trim();
  const roomId = String(truthLobby?.lobbyId ?? "").trim();

  useEffect(() => {
    if (!sessionId || !roomId) return undefined;
    const col = collection(
      database,
      LOBBY_COLLECTION,
      sessionId,
      "rooms",
      roomId,
      LOBBY_USERDATA
    );
    const unsub = onSnapshot(
      col,
      (snap) => {
        setErr("");
        const m = new Map();
        snap.docs.forEach((d) => {
          m.set(d.id, d.data());
        });
        setUserMap(m);
      },
      (e) => {
        console.error("truth leaderboard:", e);
        setErr(e?.message || "Could not load scores.");
      }
    );
    return () => unsub();
  }, [sessionId, roomId]);

  const playerIcons = useMemo(() => {
  const icons = [icon1, icon2, icon3, icon4, icon5, icon6, icon7, icon8];
  const iconMap = new Map();
  userMap.forEach((data, id) => {
    const hash = id.split('').reduce((a, b) => ((a << 5) - a + b.charCodeAt(0)) | 0, 0);
    iconMap.set(id, icons[Math.abs(hash) % icons.length]);
  });
  return iconMap;
}, [userMap]);

  const rows = useMemo(() => {
    const list = [];
    userMap.forEach((data, id) => {
      const timeTakenSeconds = Number(data?.lastGuessTimeSeconds ?? 0) || 0;
      const minutes = Math.floor(timeTakenSeconds / 60);
      const seconds = timeTakenSeconds % 60;
      const formattedTime = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      
      list.push({
        id,
        name: displayNameForUser(id, data),
        score: Number(data?.truthGameScore ?? 0) || 0,
        timeTaken: formattedTime,
        timeTakenSeconds,
        icon: playerIcons.get(id),
      });
    });
    list.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
    return list;
  }, [userMap, playerIcons]);

  const missing = !sessionId || !roomId;

  return (
    <div className="tglb">
      <MonkeyBackdrop />
      <div className="tglb__glow" aria-hidden />
      
      {/* Header Section */}
      <div className="tglb__header">
        <div className="tglb__header-content">
          <div className="tglb__trophy tglb__trophy--left">
            <img src={trophyImage} alt="Trophy" className="tglb__trophy-image" />
          </div>
          <div className="tglb__header-text">
            <h1 className="tglb__title">Leaderboard</h1>
            <p className="tglb__sub">Game Complete - Final Scores</p>
          </div>
          <div className="tglb__trophy tglb__trophy--right">
            <img src={trophyImage} alt="Trophy" className="tglb__trophy-image" />
          </div>
        </div>
      </div>

      {missing ? (
        <p className="tglb__empty">
          No lobby in session. Open a lobby from the user page, then play again.
        </p>
      ) : err ? (
        <p className="tglb__err" role="alert">
          {err}
        </p>
      ) : rows.length === 0 ? (
        <p className="tglb__empty">No players in this room yet.</p>
      ) : (
        <div className="tglb__leaderboard">
          {/* All Players */}
          <div className="tglb__players-section">
            {rows.map((r, i) => (
              <div 
                key={r.id} 
                className={`tglb__player-card ${i < 3 ? `tglb__player-card--top-${i + 1}` : ''}`}
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="tglb__player-rank">
                  <span className="tglb__rank-number">{i + 1}</span>
                </div>
                <div className="tglb__player-info">
                  <div className="tglb__player-avatar">
                    <img src={r.icon} alt={r.name} className="tglb__player-icon" />
                  </div>
                  <div className="tglb__player-details">
                    <h3 className="tglb__player-name">{r.name}</h3>
                    {i < 3 && (
                      <div className="tglb__player-title">
                        {i === 0 && "Bluff Master"}
                        {i === 1 && "Truth Sleuth"}
                        {i === 2 && "Sneaky Snake"}
                      </div>
                    )}
                  </div>
                </div>
                <div className="tglb__player-stats">
                  <div className="tglb__player-score">
                    <span className="tglb__score-value">{r.score}</span>
                    <span className="tglb__score-text">pts</span>
                  </div>
                  <div className="tglb__player-time">
                    <span className="tglb__time-value">{r.timeTaken}</span>
                    <span className="tglb__time-text"> Minutes</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* <div className="tglb__actions">
        <button
          type="button"
          className="tglb__btn tglb__btn--primary"
          onClick={() => navigate("/userpage")}
        >
          Back to Lobby
        </button>
        <button
          type="button"
          className="tglb__btn tglb__btn--secondary"
          onClick={() => navigate("/intro")}
        >
          New Game
        </button>
      </div> */}
      {showConfetti && <LeaderboardConfetti />}
    </div>
  );
}

export default Leaderboard;